// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
//
// EXTRACTION PLAN -- required by the expert-legibility rule in
// `.claude/rules/anti-ai-slop.md`: this module is past the 400-line threshold
// and this PR appends new behavior to it (the `readOnly` prop with its three
// withhold branches, and the mount-scoped comment-context cleanup).
//
// ALREADY EXTRACTED: `VersionPicker` -> `./VersionPicker.tsx`.
// The pane switcher is `AskV2ReviewPaneTabs`. The findings filter state ->
// `./useAskV2ReviewCommentFilter.ts`. The version comparison's state ->
// `./useAskV2ReviewVersionCompare.ts`, its status strip ->
// `./AskV2ReviewCompareStatus.tsx`.
//
// STILL PENDING: the two `setCommentContext` / `clearCommentContexts` effects
// and the `clearCommentContextsRef` unmount cleanup ->
// `./useAskV2ReviewCommentContext.ts`, joining the hooks this folder already
// keeps beside the component (`useAskV2ReviewWorkspace`,
// `useAskV2ReviewSidebarAutoCollapse`). ~60 lines.
//
// WHEN: as its own behavior-neutral PR, landing before the next behavior is
// appended here. The earlier-section wiring and the numbering the list and
// the pins share were the last additions this plan allows.
import { ASK_PUNTT_COMMENT_CONTEXT_TITLE_MAX_CHARS } from "@mg/schemas/src/christo/askPuntt";
import { CircleNotch, Columns, XCircle } from "@phosphor-icons/react";
import { Button, IconButton, Text } from "@radix-ui/themes";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewCommentsList } from "./AskV2ReviewCommentsList";
import { AskV2ReviewCompare } from "./AskV2ReviewCompare";
import { AskV2ReviewCompareChanges } from "./AskV2ReviewCompareChanges";
import { AskV2ReviewCompareStatus } from "./AskV2ReviewCompareStatus";
import { AskV2ReviewComposerProvider } from "./AskV2ReviewComposerContext";
import { AskV2ReviewLiveFindings } from "./AskV2ReviewLiveFindings";
import { AskV2ReviewPanes } from "./AskV2ReviewPanes";
import { AskV2ReviewPaneTabs } from "./AskV2ReviewPaneTabs";
import { AskV2ReviewPreviouslyRaised } from "./AskV2ReviewPreviouslyRaised";
import { type RejectionScope } from "./rememberedSentence";
import {
  type CommentWithOptionalTags,
  reviewCommentTags,
} from "./reviewCommentTags";
import {
  useAskPunttReview,
  useCreateAskPunttReviewComment,
  useReopenAskPunttReviewResolved,
  useSetAskPunttReviewChangeReviewed,
  useUpdateAskPunttReviewComment,
} from "./useAskPunttReview";
import { useAskV2ReviewCommentFilter } from "./useAskV2ReviewCommentFilter";
import { useAskV2ReviewVersionCompare } from "./useAskV2ReviewVersionCompare";
import { useAskV2ReviewWorkspace } from "./useAskV2ReviewWorkspace";
import { useLoadedReviewCommentGroups } from "./useLoadedReviewCommentGroups";
import { COMPARE_CHANGE_PREFIX, comparePinTone } from "./versionChanges";
import { type ReviewVersionOption, VersionPicker } from "./VersionPicker";

import {
  AskPunttArtifactProvider,
  AskPunttArtworkViewer,
  type AskPunttArtifactContextValue,
  type HumanCommentSubmitInput,
  reviewCommentTitle,
  useReviewArtworkFocus,
} from "../../components/ask-puntt-review";
import { LinkedArtworkViewProvider } from "../../components/ask-puntt-review/linkedArtworkView";
import { mapReviewFilesToAssets } from "../../components/ask-puntt-review/mapReviewAssets";
import { useAnalytics } from "../../utils/analytics";
import { useAutoScroll } from "../../utils/useAutoScroll";
import {
  askV2ReviewPanelComments,
  reviewCommentGroup,
} from "../../utils/visibleReviewComments";
import { TextSelectionToolbar } from "../transport/TextSelectionToolbar";
import { AskV2Thread } from "../transport/Thread";
import { type AskV2TransportApi } from "../transport/useAskV2Transport";

type FeedMember = {
  _id: string;
  name?: string;
  email?: string;
  avatar?: string | null;
};

type AskV2ReviewWorkspaceProps = {
  workspace: ReturnType<typeof useAskV2ReviewWorkspace>;
  transport: AskV2TransportApi;
  versionOptions?: readonly ReviewVersionOption[];
  feedPresentation?: "chat" | "feed";
  feedActorName?: string;
  feedActorAvatar?: string;
  feedMembers?: FeedMember[];
  currentUserId?: string;
  authorNameByUserId?: Readonly<Record<string, string>>;
  artifactContext?: AskPunttArtifactContextValue;
  /** Chat composer pinned under the left findings column only. */
  composer?: ReactNode;
  /**
   * The viewer may not write to the chat behind this review. The transport the
   * page hands us is already gated, but three of this workspace's writes never
   * ride `AskV2TransportApi`, so the gate cannot reach them and each needs the
   * prop: the feed thread's per-row thumbs (`submitFeedback`), the artwork
   * pane's human-comment composer (`handleSubmitHumanComment` →
   * `createReviewComment`) and the findings-list judge controls
   * (`handleJudgeComment` → `updateReviewComment`). All three are withheld
   * here.
   *
   * Defence in depth, not authorization: christo authorizes the two review
   * comment routes on enterprise scope alone (see the scope note on
   * `canInteract` in AskV2ChatPage.tsx), so those POSTs would still succeed if
   * a caller reached them another way. Making the read-only posture real needs
   * a membership check inside `createReviewComment` / `updateReviewComment`.
   */
  readOnly?: boolean;
};

// why: one identity while the review loads, so the filter's memos do not
// recompute on every render of a workspace that has no comments yet.
const EMPTY_COMMENTS: readonly CommentWithOptionalTags[] = [];

export function AskV2ReviewWorkspace({
  workspace,
  transport,
  versionOptions = [],
  feedPresentation = "chat",
  feedActorName,
  feedActorAvatar,
  feedMembers,
  currentUserId,
  authorNameByUserId,
  artifactContext,
  composer,
  readOnly = false,
}: AskV2ReviewWorkspaceProps) {
  const { t } = useTranslation("ui");
  const reviewQuery = useAskPunttReview(workspace.reviewId);
  const review = reviewQuery.data?.review;
  const artworkFiles = useMemo(
    () => mapReviewFilesToAssets(review?.files ?? []),
    [review?.files],
  );
  const contextFiles = useMemo(
    () => mapReviewFilesToAssets(review?.contextAssets ?? []),
    [review?.contextAssets],
  );

  // why: syncing comment selection to the /comments/:id route remounts the page
  // and resets the findings list scroll position; keep selection local while open.
  const artworkFocus = useReviewArtworkFocus({
    comments: review?.comments,
    initialCommentId: workspace.commentId,
  });

  const commentFilter = useAskV2ReviewCommentFilter({
    comments: review?.comments ?? EMPTY_COMMENTS,
    authorNameByUserId,
  });
  const { isVisible: isCommentFilterVisible } = commentFilter;
  const [judgmentFailed, setJudgmentFailed] = useState(false);

  const tagFilteredComments = useMemo(() => {
    if (!review) return [];
    return review.comments.filter(isCommentFilterVisible);
  }, [isCommentFilterVisible, review]);

  // The section each finding sat in when this review loaded; the list groups
  // by it and the numbers below follow it, so a judgment made here moves no
  // card and renumbers none.
  const loadedGroups = useLoadedReviewCommentGroups(
    review?.comments,
    review?.reviewId,
  );

  // why: a finding rejected in this review stays here, grayed in its place,
  // and so stays on the artwork as a grayed pin -- the list, the pins and the
  // tab badge all read this one array.
  const panelComments = useMemo(
    () => askV2ReviewPanelComments(tagFilteredComments, loadedGroups),
    [loadedGroups, tagFilteredComments],
  );

  // Whether the turn behind this review is still running. The backend keeps
  // confirming/clearing it on the review itself (`aiRunning`), and the query
  // polls while it is set, so this flips to false on its own within a poll of
  // the turn ending.
  const isReviewing = review?.aiRunning === true;
  // How many of the turn's checks have finished, off the pending message the
  // chat is already carrying (`checkProgress`, written by the warhol progress
  // mirror). Read off `historyMessages` — the server's own messages, not the
  // local thread's — because `checkProgress` is a server field the transport
  // does not carry into the local shape. Absent on a backend that does not
  // publish it yet, and absent before the turn's first leg settles: the banner
  // then simply says less rather than guessing.
  const checksFinished = useMemo(() => {
    const messages = transport.historyMessages ?? [];
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const progress = messages[index].checkProgress;
      if (progress?.length) {
        return progress.filter((check) => check.status === "done").length;
      }
    }
    return 0;
  }, [transport.historyMessages]);

  const {
    selectedCommentId,
    selectComment,
    clearComment,
    artworkPaneRef,
    artworkHeaderControls,
    setArtworkHeaderControls,
  } = artworkFocus;

  // why: numbered once over every finding the panel holds, in the order of the
  // section each sat in when the review loaded, and handed to the list and the
  // pins alike, so a card and its pin carry one number.
  const commentNumbers = useMemo(() => {
    const numbers: Record<string, number> = {};
    let index = 0;
    for (const comment of panelComments) {
      if (comment.isDeleted) continue;
      index += 1;
      numbers[comment._id] = index;
    }
    return numbers;
  }, [panelComments]);

  const { mutateAsync: createReviewComment } = useCreateAskPunttReviewComment(
    workspace.reviewId,
  );
  const { mutateAsync: updateReviewComment, isPending: isJudgmentPending } =
    useUpdateAskPunttReviewComment(workspace.reviewId);
  const analytics = useAnalytics();
  const [pendingFocusCommentId, setPendingFocusCommentId] = useState<
    string | null
  >(null);
  const { setLeftPane } = workspace;
  const [composerFocusNonce, setComposerFocusNonce] = useState(0);
  const focusedCommentIdRef = useRef<string | null>(null);
  // why: the comment row takes the click, so the caret has to move into the
  // composer after selection or the user still has to click the field.
  useEffect(() => {
    if (!selectedCommentId) {
      focusedCommentIdRef.current = null;
      return;
    }
    if (focusedCommentIdRef.current === selectedCommentId) return;
    focusedCommentIdRef.current = selectedCommentId;
    setComposerFocusNonce((current) => current + 1);
  }, [selectedCommentId]);
  const { setCommentContext, clearCommentContexts, removeCommentContext } =
    transport;
  const handleRemoveCommentContext = useCallback(
    (id: string) => {
      removeCommentContext(id);
      clearComment();
    },
    [clearComment, removeCommentContext],
  );
  const handleSubmitHumanComment = useCallback(
    async (input: HumanCommentSubmitInput) => {
      const { comment } = await createReviewComment({
        description: input.description,
        rel_x: input.relX,
        rel_y: input.relY,
        page_num: input.page,
      });
      setPendingFocusCommentId(comment._id);
      setLeftPane("comments");
    },
    [createReviewComment, setLeftPane],
  );

  // why: the saved comment reaches `review.comments` one render after the
  // mutation resolves; selecting it synchronously misses the focus lookup.
  useEffect(() => {
    if (pendingFocusCommentId == null) return;
    if (!review?.comments.some((c) => c._id === pendingFocusCommentId)) {
      return;
    }
    selectComment(pendingFocusCommentId);
    setPendingFocusCommentId(null);
  }, [pendingFocusCommentId, review?.comments, selectComment]);

  useEffect(() => {
    if (!selectedCommentId || !review) return;
    const comment = review.comments.find(
      (item) => item._id === selectedCommentId,
    );
    const onPanel = panelComments.some((c) => c._id === selectedCommentId);
    if (!comment || !isCommentFilterVisible(comment) || !onPanel) {
      clearComment();
    }
  }, [
    clearComment,
    isCommentFilterVisible,
    panelComments,
    review,
    selectedCommentId,
  ]);

  useEffect(() => {
    if (!selectedCommentId || !review) {
      clearCommentContexts();
      return;
    }
    const comment = review.comments.find(
      (item) => item._id === selectedCommentId,
    );
    const onPanel = panelComments.some((c) => c._id === selectedCommentId);
    if (!comment || !isCommentFilterVisible(comment) || !onPanel) {
      clearCommentContexts();
      return;
    }
    const number = commentNumbers[selectedCommentId] ?? 0;
    const title = reviewCommentTitle(comment);
    const normalizedTitle =
      title.length <= ASK_PUNTT_COMMENT_CONTEXT_TITLE_MAX_CHARS
        ? title
        : `${title.slice(0, ASK_PUNTT_COMMENT_CONTEXT_TITLE_MAX_CHARS - 3)}...`;
    const tags = reviewCommentTags(comment);
    setCommentContext({
      id: `review-comment-${selectedCommentId}`,
      commentId: selectedCommentId,
      reviewId: review.reviewId,
      number,
      title: normalizedTitle,
      tag: tags[0],
    });
  }, [
    clearCommentContexts,
    commentNumbers,
    isCommentFilterVisible,
    panelComments,
    review,
    selectedCommentId,
    setCommentContext,
  ]);

  // why: the chip lives on the PAGE transport, which outlives this pane — the
  // composer the review column hosts is the same SummonBar the project feed
  // shows once the review closes. The effect above only clears while the
  // workspace is mounted, so closing the review with a finding selected left
  // that finding attached to the next message sent from the feed, and
  // `send()` fires even on an empty composer while a comment context is
  // attached. A ref, not a dep: a cleanup keyed on the transport callback
  // would re-run on every identity change and wipe the chip the effect above
  // had just set.
  const clearCommentContextsRef = useRef(clearCommentContexts);
  clearCommentContextsRef.current = clearCommentContexts;
  useEffect(() => () => clearCommentContextsRef.current(), []);

  const seenSendingUserIdsRef = useRef(new Set<string>());

  useEffect(() => {
    for (const message of transport.messages) {
      if (message.role !== "user" || message.status !== "sending") continue;
      if (seenSendingUserIdsRef.current.has(message.clientId)) continue;
      seenSendingUserIdsRef.current.add(message.clientId);
      if ((message.commentContexts?.length ?? 0) > 0) {
        clearComment();
      }
      if (workspace.leftPane === "comments") {
        workspace.setLeftPane("feed");
      }
    }
  }, [
    clearComment,
    transport.messages,
    workspace.leftPane,
    workspace.setLeftPane,
  ]);

  const handleJudgeComment = useCallback(
    async (
      commentId: string,
      liked: boolean | null,
      scope?: RejectionScope,
    ) => {
      const visibleList = panelComments;
      const index = visibleList.findIndex((item) => item._id === commentId);
      const judged = visibleList.find((item) => item._id === commentId);
      const previousLiked = judged?.liked ?? null;
      // why: `liked: null` arrives from Restore on a rejected finding AND from
      // a second Confirm press that clears a confirmation; only the first is a
      // restore. Read off the section, because a past review's rejection
      // leaves `liked` null.
      const isRestore =
        liked === null &&
        judged != null &&
        reviewCommentGroup(judged) === "rejected";

      analytics.capture("ask_puntt_feedback", {
        feedback:
          liked === true
            ? "comment_thumbs_up"
            : liked === false
              ? "comment_thumbs_down"
              : isRestore
                ? "comment_restore"
                : "comment_unconfirm",
        comment_id: commentId,
        review_id: workspace.reviewId,
      });

      // The scope rides with the verdict rather than in a second call: two
      // writes could land a rejection with no scope, which is the one state
      // that binds every brand.
      setJudgmentFailed(false);
      try {
        await updateReviewComment({
          commentId,
          body: scope ? { liked, judgmentScope: scope } : { liked },
        });
      } catch {
        // why: stay on the finding and say so. Nothing was written and the
        // card keeps its prior state; advancing would read as saved, and the
        // reject prompt has already closed, so without this line a failed
        // save is invisible.
        setJudgmentFailed(true);
        return;
      }

      if (liked === null) return;

      if (liked === true && previousLiked !== true) {
        const next = visibleList[index + 1];
        if (next) selectComment(next._id);
        return;
      }

      if (liked === false) {
        // why: the rejected card keeps its place, grayed, so moving on never
        // hides it. Skip findings already rejected, here or in a past review
        // (not restored): landing on one would open a decision already made.
        const isOpen = (item: (typeof visibleList)[number]) =>
          item._id !== commentId && reviewCommentGroup(item) !== "rejected";
        const next =
          visibleList.slice(index + 1).find(isOpen) ??
          visibleList.slice(0, Math.max(index, 0)).reverse().find(isOpen);
        if (next != null) {
          selectComment(next._id);
        } else {
          clearComment();
        }
      }
    },
    [
      analytics,
      clearComment,
      panelComments,
      selectComment,
      updateReviewComment,
      workspace.reviewId,
    ],
  );

  const resolvedVersionOptions = useMemo(() => {
    if (versionOptions.length > 0) return versionOptions;
    if (!review) return [];
    return [{ reviewId: review.reviewId, title: review.title }];
  }, [review, versionOptions]);

  // SPIKE (side-by-side version compare): another review of this chat shown
  // beside this one. The findings column stays this review's.
  const compare = useAskV2ReviewVersionCompare({
    review,
    versionOptions: resolvedVersionOptions,
    selectedCommentId,
    historyMessages: transport.historyMessages ?? [],
    panelComments,
    commentNumbers,
  });
  const {
    canCompare,
    isComparing,
    compareReviewId,
    comparison,
    changedRegions,
    newerReview,
    changeAt,
    steppedRegion,
    steppedPin,
    selectedComment,
  } = compare;
  const setChangeReviewed = useSetAskPunttReviewChangeReviewed();
  const reopenResolved = useReopenAskPunttReviewResolved();
  const { versionMark } = compare;

  // In a comparison each version's picker sits over its own pane and the one
  // toolbar sits over the compared pane, so both are built once and placed.
  const versionPicker = review ? (
    <VersionPicker
      review={review}
      options={resolvedVersionOptions}
      onSelect={workspace.switchReview}
      mark={versionMark(review.reviewId)}
    />
  ) : null;
  const headerControls = (
    <div
      className="ml-auto flex items-center gap-2"
      data-testid="ask-v2-review-header-controls"
    >
      {artworkHeaderControls}
    </div>
  );

  const {
    scrollContainerRef: feedScrollContainerRef,
    contentRef: feedContentRef,
    handleScroll: handleFeedScroll,
  } = useAutoScroll(
    // why: the feed container is mounted only on the feed pane, but the
    // composer that sends into it is on both. Without leftPane here, a send
    // made from the comments pane arms the pin against a container that does
    // not exist yet, and nothing re-runs the effect when the pane switches —
    // so the feed opens at the top, on the oldest messages.
    [transport.displayMessages, workspace.leftPane],
    transport.chatId,
    {
      enabled: !transport.inFlight,
      // why: the sent turn can render while the sticky scroll guard is still
      // latched, and nothing in ask-v2 clears it on send — so without an
      // explicit send signal the turn lands below the fold and the reader has
      // to scroll to find it. (The `enabled` hold is not the blocker on that
      // first commit: send() writes `sending` and the optimistic row in one
      // mutate and does not recompute `inFlight`.)
      sentSignal: transport.sending,
    },
  );

  if (reviewQuery.isLoading) {
    return (
      <div
        className="flex flex-1 items-center justify-center"
        data-testid="ask-v2-review-loading"
      >
        <CircleNotch
          size={24}
          weight="bold"
          className="animate-spin text-puntt-neutral-gray-9"
        />
      </div>
    );
  }

  if (reviewQuery.isError || !review) {
    return (
      <div
        className="flex flex-1 items-center justify-center px-6 text-center text-sm text-puntt-neutral-gray-10"
        data-testid="ask-v2-review-error"
      >
        {t("askV2.review.loadError")}
      </div>
    );
  }

  return (
    <div
      className="flex size-full min-h-0 flex-1 overflow-hidden"
      data-testid="ask-v2-review-workspace"
      // why: a `<div>` with no role is `role="generic"`, and ARIA prohibits
      // `aria-label` there (axe `aria-prohibited-attr`) -- the name was
      // computed and then dropped, so both halves of this screen reached a
      // reader as unnamed generic boxes and the artwork pane below could not
      // be told apart from the findings column. The two names this component
      // already pays for only exist once something owns them; `region` is what
      // the findings list itself uses (`<section aria-label>` in
      // AskV2ReviewCommentsList) and what CommentLoginWall/TimeSavedBubble use
      // for the same job.
      role="region"
      aria-label={t("askV2.review.workspaceLabel")}
    >
      <AskV2ReviewPanes
        abortSignal={review.reviewId}
        left={
          <div className="ask-puntt-surface flex h-full min-h-0 min-w-0 flex-col">
            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
              {workspace.leftPane === "comments" && judgmentFailed ? (
                <Text
                  as="p"
                  size="2"
                  color="red"
                  role="alert"
                  className="px-4 pt-3"
                  data-testid="ask-v2-review-judgment-error"
                >
                  {t("assetViewer.judgmentSaveFailed")}
                </Text>
              ) : null}
              {workspace.leftPane === "comments" && isReviewing ? (
                <div
                  role="status"
                  aria-live="polite"
                  className="mx-4 mt-3 flex items-start gap-2 rounded-md border border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 px-3 py-2"
                  data-testid="ask-v2-review-still-reviewing"
                >
                  <CircleNotch
                    size={16}
                    weight="bold"
                    className="mt-0.5 shrink-0 animate-spin text-puntt-neutral-gray-9"
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <Text
                      as="p"
                      size="2"
                      weight="medium"
                      data-testid="ask-v2-review-still-reviewing-title"
                    >
                      {t("askV2.review.stillReviewing")}
                    </Text>
                    <Text
                      as="p"
                      size="1"
                      color="gray"
                      data-testid="ask-v2-review-still-reviewing-hint"
                    >
                      {t("askV2.review.stillReviewingHint")}
                      {checksFinished > 0
                        ? ` ${t("askV2.review.stillReviewingChecks", {
                            done: checksFinished,
                          })}`
                        : ""}
                    </Text>
                  </div>
                </div>
              ) : null}
              {workspace.leftPane === "comments" && isReviewing ? (
                <AskV2ReviewLiveFindings
                  findings={transport.streamSnapshot?.verifiedFindings}
                  // why: the join must be built from the comments the panel
                  // below actually draws, not every comment christo served.
                  // Built from `review.comments`, a finding a tag filter hides
                  // kept its openable "Found so far" row: the click ran
                  // `selectComment` and jumped the artwork pane while no card
                  // existed in the list to land on. A finding she rejected
                  // mid-turn is drawn (grayed), so its row stays openable.
                  comments={panelComments}
                  onOpenComment={selectComment}
                />
              ) : null}
              {workspace.leftPane === "comments" &&
              compare.showChanges &&
              changedRegions ? (
                // why: the column follows the pins. With the comparison showing
                // its changes, a list of findings beside numbered windows read
                // as if finding 3 were change 3 (story: the compare screen).
                <AskV2ReviewCompareChanges
                  regions={changedRegions}
                  selectedIndex={changeAt}
                  onSelect={compare.setChangeAt}
                  // The open viewer's page; the linked compared one follows it.
                  onLocate={(region) =>
                    artworkPaneRef.current?.navigateToPage(region.page)
                  }
                  beforeMark={compare.compareSideMark("before")}
                  afterMark={compare.compareSideMark("after")}
                  reviewed={newerReview ? compare.reviewedNumbers : undefined}
                  onToggleReviewed={
                    newerReview && comparison
                      ? (number, reviewed) =>
                          setChangeReviewed.mutate({
                            reviewId: newerReview.reviewId,
                            comparisonId: comparison.id,
                            number,
                            reviewed,
                          })
                      : undefined
                  }
                />
              ) : workspace.leftPane === "comments" ? (
                <AskV2ReviewCommentsList
                  comments={panelComments}
                  commentNumbers={commentNumbers}
                  // why: the list unmounts on a pane switch; its own snapshot
                  // would then file a card judged here into its new section.
                  commentGroups={loadedGroups}
                  contextFiles={contextFiles}
                  reviewFiles={artworkFiles}
                  selectedCommentId={selectedCommentId}
                  authorNameByUserId={authorNameByUserId}
                  currentUserId={currentUserId}
                  currentUserName={feedActorName}
                  activeTags={commentFilter.activeTags}
                  showTagFilter={commentFilter.showTagFilter}
                  onToggleTag={commentFilter.toggleTag}
                  sourceFilter={commentFilter.sourceFilter}
                  judgmentPending={isJudgmentPending}
                  // why: the judge controls POST `updateReviewComment` outside the
                  // transport, so the page's gate cannot reach them. Withholding
                  // the handler is how the list stops drawing them.
                  onJudge={readOnly ? undefined : handleJudgeComment}
                  onSelect={selectComment}
                  onClear={clearComment}
                  afterUnresolved={
                    review ? (
                      <AskV2ReviewPreviouslyRaised
                        resolved={review.resolved ?? []}
                        versionNumber={review.versionNumber}
                        onViewChange={
                          canCompare ? compare.viewResolvedChange : undefined
                        }
                        onReopen={
                          readOnly
                            ? undefined
                            : (commentId) =>
                                reopenResolved.mutate({
                                  reviewId: review.reviewId,
                                  commentId,
                                  reopened: true,
                                })
                        }
                        reopenPending={reopenResolved.isPending}
                      />
                    ) : null
                  }
                />
              ) : (
                <div
                  ref={feedScrollContainerRef}
                  onScroll={handleFeedScroll}
                  className="relative min-h-0 flex-1 overflow-y-auto px-4 py-6"
                  data-testid="ask-v2-review-feed-scroll"
                  // why: a memory chip's card keeps inside this column, scrolling yet or not, so a short feed never lets it slide over the artwork pane (`popoverBoundary`).
                  data-popover-boundary
                >
                  <TextSelectionToolbar
                    containerRef={feedScrollContainerRef}
                    onAddContext={transport.addQuotedContext}
                  />
                  <div ref={feedContentRef}>
                    {artifactContext ? (
                      <AskPunttArtifactProvider value={artifactContext}>
                        <AskV2Thread
                          transport={transport}
                          presentation={feedPresentation}
                          feedActorName={feedActorName}
                          feedActorAvatar={feedActorAvatar}
                          feedMembers={feedMembers}
                          currentUserId={currentUserId}
                          readOnly={readOnly}
                        />
                      </AskPunttArtifactProvider>
                    ) : (
                      <AskV2Thread
                        transport={transport}
                        presentation={feedPresentation}
                        feedActorName={feedActorName}
                        feedActorAvatar={feedActorAvatar}
                        feedMembers={feedMembers}
                        currentUserId={currentUserId}
                        readOnly={readOnly}
                      />
                    )}
                  </div>
                </div>
              )}
              {composer ? (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-5 bg-[linear-gradient(to_top,var(--ask-puntt-surface-bg)_15%,transparent)]"
                  data-testid="ask-v2-review-comment-list-fade"
                />
              ) : null}
            </div>
            {composer ? (
              <div
                className="shrink-0 p-4"
                data-testid="ask-v2-review-composer"
              >
                <AskV2ReviewComposerProvider
                  onRemoveCommentContext={handleRemoveCommentContext}
                  leftPane={workspace.leftPane}
                  commentCount={panelComments.length}
                  onPaneChange={workspace.setLeftPane}
                  composerFocusNonce={composerFocusNonce}
                >
                  {composer}
                </AskV2ReviewComposerProvider>
              </div>
            ) : null}
          </div>
        }
        right={
          <div
            className="flex min-h-0 min-w-0 flex-[3] flex-col overflow-hidden bg-puntt-neutral-gray-2"
            data-testid="ask-v2-review-artwork"
            // why: same prohibited-attribute drop as the workspace wrapper --
            // "Artwork" is the only thing telling this pane apart from the
            // findings column, and a bare `<div>` may not carry a name.
            role="region"
            aria-label={t("askV2.review.artworkLabel")}
          >
            <div className="flex items-center gap-3 border-b border-puntt-neutral-gray-5 bg-base-white px-4 py-2">
              <IconButton
                variant="ghost"
                color="gray"
                size="1"
                data-testid="ask-v2-review-close"
                aria-label={t("askV2.review.closeLabel")}
                onClick={workspace.closeReview}
              >
                <XCircle size={16} weight="bold" aria-hidden="true" />
              </IconButton>
              {isComparing ? null : versionPicker}
              {canCompare && !isComparing ? (
                <Button
                  variant="soft"
                  color="gray"
                  size="1"
                  data-testid="ask-v2-review-compare-toggle"
                  aria-label={t("askV2.review.compareToggleLabel")}
                  onClick={() => compare.setComparing(true)}
                >
                  <Columns size={14} weight="bold" aria-hidden="true" />
                  {t("askV2.review.compareToggle")}
                </Button>
              ) : null}
              <AskV2ReviewPaneTabs
                data-testid="ask-v2-review-pane-tabs"
                leftPane={workspace.leftPane}
                // why: the badge counts every finding the list draws and the pins
                // show. Counting `review.comments` instead let the tab read
                // "Comments (3)" beside a list of 2 after a reject.
                commentCount={panelComments.length}
                onChange={workspace.setLeftPane}
              />
              {isComparing ? null : headerControls}
            </div>
            {isComparing && compare.comparedQuery.data ? (
              <AskV2ReviewCompareStatus
                comparison={comparison}
                changeAt={changeAt}
                steppedRegion={steppedRegion}
                hasSelectedComment={
                  selectedComment != null && compare.canPlaceFinding
                }
                selectedRegion={compare.selectedRegion}
                pinsShown={compare.pinsShown}
                onStep={compare.stepChange}
                onShowPins={compare.showPins}
              />
            ) : null}
            <LinkedArtworkViewProvider enabled={isComparing}>
              <div className="flex min-h-0 flex-1">
                {isComparing && compareReviewId ? (
                  <AskV2ReviewCompare
                    reviewId={compareReviewId}
                    options={compare.compareOptions}
                    onSelect={compare.setCompareChoice}
                    selectedComment={steppedPin ?? selectedComment}
                    selectedNumber={
                      steppedRegion
                        ? steppedRegion.number
                        : selectedCommentId
                          ? commentNumbers[selectedCommentId]
                          : undefined
                    }
                    currentUserId={currentUserId}
                    toolbar={headerControls}
                    mark={versionMark(compareReviewId)}
                    side={compare.comparedSide}
                    changes={compare.comparedChanges}
                    // Only a changed window answers here: this version's own
                    // findings are not the open review's, and have no card.
                    onPinClick={compare.stepToPin}
                  />
                ) : null}
                <div
                  className="flex min-h-0 min-w-0 flex-1 flex-col"
                  data-testid="ask-v2-review-artwork-current"
                >
                  {isComparing ? (
                    <div
                      className="flex items-center gap-3 border-b border-puntt-neutral-gray-5 bg-base-white px-4 py-2"
                      data-testid="ask-v2-review-compare-current-header"
                    >
                      {versionPicker}
                      <Button
                        variant="outline"
                        color="gray"
                        size="1"
                        className="ml-auto"
                        data-testid="ask-v2-review-compare-close"
                        onClick={() => compare.setComparing(false)}
                      >
                        {t("askV2.review.compareCloseLabel")}
                      </Button>
                    </div>
                  ) : null}
                  <div className="min-h-0 flex-1">
                    <AskPunttArtworkViewer
                      ref={artworkPaneRef}
                      reviewId={review.reviewId}
                      title={review.title}
                      files={artworkFiles}
                      comments={compare.viewerComments}
                      commentNumbers={compare.viewerNumbers}
                      currentUserId={currentUserId}
                      activeCommentId={steppedPin?._id ?? selectedCommentId}
                      pinTone={comparePinTone(compare.openSide)}
                      onHeaderControlsChange={setArtworkHeaderControls}
                      onCommentClick={(comment) => {
                        // A changed window is not a finding: clicking its pin
                        // steps to it, and there is nothing to open in the column.
                        if (comment._id.startsWith(COMPARE_CHANGE_PREFIX)) {
                          compare.stepToPin(comment);
                          return;
                        }
                        workspace.setLeftPane("comments");
                        selectComment(comment._id);
                      }}
                      onArtworkBackgroundClick={() => {
                        if (selectedCommentId != null) {
                          clearComment();
                        }
                      }}
                      // why: same as the judge controls — this composer POSTs
                      // `createReviewComment` off the transport. Without the
                      // handler the viewer falls back to the read-only placement
                      // trigger and a bare click no longer drops a draft pin.
                      onSubmitHumanComment={
                        readOnly ? undefined : handleSubmitHumanComment
                      }
                    />
                  </div>
                </div>
              </div>
            </LinkedArtworkViewProvider>
          </div>
        }
      />
    </div>
  );
}
